/** Every sentence the AI models page prints (SA-1087 §12). */
import { ApiError } from '@/services/apiClient'
import type { AiModelFailure, AiModelFunction, AiModelSource } from '@/types/aiModels'

export const PAGE_TITLE = 'AI models'
export const PAGE_SENTENCE = 'Which model each AI function calls. Personas choose their own.'
export const UNAVAILABLE = 'The AI models could not be loaded.'
export const COLUMNS = ['Function', 'Provider', 'Model', 'Effort', 'Key', 'Source'] as const
export const ACTIONS = 'Actions'
export const MODEL_DEFAULT_EFFORT = 'Model default'
export const TOOLS_HINT = 'GPT-5.4, 5.6 and 6 models take tools at effort none only.'
export const PROVIDER = 'Provider'
export const BASE_URL = 'Base URL'
export const MODEL = 'Model'
export const EFFORT = 'Reasoning effort'
export const KEY_SOURCE = 'Key'
export const ACTOR_KEY = (actor: string) => `The key of ${actor}`
export const STORED_KEY = 'A key stored here'
export const API_KEY = 'API key'
export const KEY_SET_PLACEHOLDER = 'key set'
export const KEY_PLACEHOLDER = 'Paste the key'
export const KEY_SERVICE = 'Credential service'
export const TEST = 'Test'
export const SAVE = 'Save'
export const SAVING = 'Saving…'
export const TESTING = 'Testing…'
export const TEST_FAILED_FALLBACK = 'The test call could not be made. Try again.'
export const CANCEL = 'Cancel'
export const EDIT = 'Edit'
export const USE_RECOMMENDED = 'Use recommended'
export const USE_INHERITED = 'Use inherited'
export const UNSUPPORTED = 'not supported by this function'
export const NOT_CONFIGURED = 'Not configured'
export const DASH = '—'
export const CANDIDATE_MODELS = ['gpt-5.6-terra', 'gpt-5.6-luna', 'gpt-5.4-mini', 'gpt-6-luna', 'gpt-6-sol', 'gpt-5.2']

export const SOURCE_WORDS: Record<AiModelSource, string> = {
  page: 'Saved here',
  env: 'From env',
  copilot_config: 'From copilot settings',
  doc_assistant: 'Inherited from the retired Doc assistant',
  persona: "From each persona's model",
  unconfigured: NOT_CONFIGURED,
}

export function testsWith(_fn: AiModelFunction, providerLabel: string): string {
  return `The test runs with your own ${providerLabel} key.`
}

export function recommendedLine(fn: AiModelFunction): string | null {
  if (!fn.recommended) return null
  return `Recommended: ${fn.recommended.model} · ${fn.recommended.effort ?? MODEL_DEFAULT_EFFORT.toLowerCase()}`
}

export function testOk(latencyMs: number): string {
  return `The test call answered in ${latencyMs} ms.`
}

export function editLabel(fn: AiModelFunction): string {
  return `Edit ${fn.label}`
}

export const BASE_URL_PLACEHOLDER = 'https://llm.example/v1'
export const KEY_SERVICE_PLACEHOLDER = 'openai'
export const LOAD_MODELS = 'Load models'
export const LOADING_MODELS = 'Loading…'
export const FORBIDDEN = 'You need the Manage AI Models permission.'
export const SAVE_FAILED = 'The change was not saved. Try again.'
export const MODELS_FAILED_FALLBACK = 'The model list could not be loaded. Try again.'
export const FAILURES_SHOWN = 3
const UNNAMED = 'an unnamed persona'
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad = (n: number) => String(n).padStart(2, '0')

/** "10 Oct, 14:02" in the viewer's own zone; "—" for a value that does not parse. */
export function whenLocal(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return DASH
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function keyWords(fn: AiModelFunction): string {
  const e = fn.effective
  if (!e) return DASH
  if (e.key_source === 'deployment') return e.key_set ? STORED_KEY : 'No key stored'
  if (!fn.actor) return "The actor's key"
  return `${fn.actor[0].toUpperCase()}${fn.actor.slice(1)}'s key`
}

export function savedByLine(fn: AiModelFunction): string | null {
  if (!fn.updated_by_name || !fn.updated_at) return null
  const d = new Date(fn.updated_at)
  if (Number.isNaN(d.getTime())) return null
  return `by ${fn.updated_by_name}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

export function failureLine(f: AiModelFailure): string {
  return `Last failed for ${f.persona_name || UNNAMED} · ${whenLocal(f.at)} · ${f.sentence}`
}

export function moreFailures(n: number): string {
  return `and ${n} more`
}

export function unreadableLine(labels: string[]): string {
  return `Saved settings for ${labels.join(', ')} could not be read and are ignored until saved again.`
}

export function modelsLoaded(n: number, providerLabel: string): string {
  return `${n} ${n === 1 ? 'model' : 'models'} from ${providerLabel}`
}

function serverMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null
  const detail = error.detail
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message
  }
  return null
}

function worded(error: unknown, fallback: string): string {
  const message = serverMessage(error)
  if (message !== null) return message
  if (error instanceof ApiError && error.status === 403) return FORBIDDEN
  return fallback
}

/** The server's sentence for a refused save or clear; never a bare code. */
export function refusalText(error: unknown): string {
  return worded(error, SAVE_FAILED)
}

/** A failed Test: the server's sentence, else the test's own (a test saves nothing). */
export function testErrorText(error: unknown): string {
  return worded(error, TEST_FAILED_FALLBACK)
}

/** A failed Load models. */
export function modelsErrorText(error: unknown): string {
  return worded(error, MODELS_FAILED_FALLBACK)
}

export const VISION_REPLACES = "Saving replaces every persona's model for this function."
export const VISION_REPLACES_PROOF =
  "Saving replaces every persona's model for this function, including each persona's own proof model."

/** The line a vision function's dialog adds under the model (SA-1115). */
export function visionReplaces(fn: AiModelFunction): string | null {
  if (!fn.vision) return null
  return fn.id === 'payment_proof' ? VISION_REPLACES_PROOF : VISION_REPLACES
}
